import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Modal, Drawer } from "antd";
import { useDispatch } from "react-redux";
import logo from "../assets/doxzon-logo.png";
import { APP_ROUTES } from "../PageRouting/pageRoutes";
import Login from "../Authentication/Login";
import SignUp from "../Authentication/SignUp";
import {
  handleLoginSignUpModal,
  LOGINMODAL,
  SIGNUPMODAL,
} from "../Redux/actions/commonActions";
import {
  Stethoscope,
  Building2,
  UserCog,
  Users,
  Pill,
  TestTube,
  FileText,
  ChevronRight,
  ShieldCheck,
  X,
} from "lucide-react";

const NAV_ITEMS = [
  { label: "Consultation", route: APP_ROUTES.CONSULTDOCTOR },
  { label: "Clinics", scrollTo: "clinic-section" },
  { label: "Providers", scrollTo: "providers-section" },
  { label: "Patients", scrollTo: "patients-section" },
  { label: "Medicine", route: APP_ROUTES.MEDICINES },
  { label: "Lab Tests", route: APP_ROUTES.LABTESTS },
  { label: "Referral ", route: APP_ROUTES.HOSPITALSERVICE },
];
const MENU_ICONS = {
  Consultation: <Stethoscope size={22} />,
  Clinics: <Building2 size={22} />,
  Providers: <UserCog size={22} />,
  Patients: <Users size={22} />,
  Medicine: <Pill size={22} />,
  "Lab Tests": <TestTube size={22} />,
  Referral: <FileText size={22} />,
};

/* ── Premium High-End Styles ─────────────────────────────────────────── */
const PREMIUM_THEME_STYLE = `
  :root[data-theme="dark"] {
    --dox-bg-primary: #0a0800;
    --dox-text-primary: #f5e6b0;
  }
  [data-theme="dark"] body { background: #0a0800 !important; color: #f5e6b0 !important; }
  
  .dox-logo { transition: filter 0.3s ease; }
  [data-theme="dark"] .dox-logo {
    filter: brightness(0) invert(1) sepia(1) saturate(4) hue-rotate(5deg) brightness(1.05);
  }

  /* Premium Antd Drawer Overrides for Modern Glassmorphism Feel */
  .premium-mobile-drawer .ant-drawer-content {
    background: rgba(255, 255, 255, 0.85) !important;
    backdrop-filter: blur(20px) !important;
    -webkit-backdrop-filter: blur(20px) !important;
  }
  [data-theme="dark"] .premium-mobile-drawer .ant-drawer-content {
    background: rgba(15, 11, 2, 0.90) !important;
    border-right: 1px solid rgba(200, 150, 30, 0.2) !important;
  }
  .premium-mobile-drawer .ant-drawer-header {
    border-bottom: 1px solid rgba(0, 0, 0, 0.05) !important;
    background: transparent !important;
  }
  [data-theme="dark"] .premium-mobile-drawer .ant-drawer-header {
    border-bottom: 1px solid rgba(200, 150, 30, 0.15) !important;
    background: transparent !important;
  }
  .premium-mobile-drawer .ant-drawer-title {
    font-weight: 700 !important;
    letter-spacing: 0.5px;
    font-size: 16px !important;
  }
  [data-theme="dark"] .premium-mobile-drawer .ant-drawer-title {
    color: #f0c040 !important;
  }
`;

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [signupOpen, setSignupOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showLoginUserTypeSwitch, setShowLoginUserTypeSwitch] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const darkMode = sessionStorage.getItem("dox-theme") === "dark";

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const id = "dox-premium-theme-style";
    if (!document.getElementById(id)) {
      const tag = document.createElement("style");
      tag.id = id;
      tag.textContent = PREMIUM_THEME_STYLE;
      document.head.appendChild(tag);
    }
  }, []);

  const syncLogin = (val, showUserTypeSwitch = false) => {
    setShowLoginUserTypeSwitch(val ? showUserTypeSwitch : false);
    setLoginOpen(val);
    dispatch(handleLoginSignUpModal({ data: val, actionType: LOGINMODAL }));
  };
  const syncSignup = (val) => {
    setSignupOpen(val);
    dispatch(handleLoginSignUpModal({ data: val, actionType: SIGNUPMODAL }));
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (item) => {
    setMobileMenuOpen(false);
    if (item.scrollTo) {
      const scrollNow = () => {
        const el = document.getElementById(item.scrollTo);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      };
      if (
        window.location.pathname === APP_ROUTES.HOME ||
        window.location.pathname === "/"
      ) {
        scrollNow();
      } else {
        navigate(APP_ROUTES.HOME);
        setTimeout(scrollNow, 400);
      }
    } else if (item.route) {
      navigate(item.route);
    }
  };

  const navBg = darkMode
    ? "rgba(15,11,2,0.82)"
    : scrolled
      ? "rgba(255,255,255,0.78)"
      : "rgba(255,255,255,0.55)";
  const navBorder = darkMode
    ? "1px solid rgba(200,150,30,0.25)"
    : "1px solid rgba(18,196,139,0.2)";

  return (
    <>
      <nav
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
        style={{
          background: navBg,
          backdropFilter: "blur(24px)",
          WebkitBackdropFilter: "blur(24px)",
          borderBottom: navBorder,
          height: "62px",
        }}
      >
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 h-full flex items-center justify-between">
          {/* ── CASE A: MOBILE LAYOUT (Zero Overlap Flow) ── */}
          {isMobile ? (
            <div className="w-full flex items-center justify-between gap-2">
              {/* Left Wing: Menu Toggle Button */}
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="flex items-center justify-center bg-transparent border-none p-2 cursor-pointer shrink-0"
                style={{ color: darkMode ? "#f0c040" : "#071a3d" }}
              >
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <line x1="3" y1="12" x2="21" y2="12"></line>
                  <line x1="3" y1="6" x2="21" y2="6"></line>
                  <line x1="3" y1="18" x2="21" y2="18"></line>
                </svg>
              </button>

              {/* Flex Center: Dynamic Auto Scaling Logo Block */}
              <div className="flex items-center justify-center flex-1 min-w-0">
                <img
                  src={logo}
                  alt="Doxzon"
                  className="dox-logo h-7 xs:h-8 max-w-full object-contain"
                />
              </div>
            </div>
          ) : (
            /* ── CASE B: WEB / DESKTOP VIEW LAYOUT ── */
            <>
              {/* Left Corner Logo */}
              <div className="flex items-center shrink-0">
                <button
                  onClick={() =>
                    window.scrollTo({ top: 0, behavior: "smooth" })
                  }
                  className="bg-transparent border-none p-0 cursor-pointer"
                >
                  <img
                    src={logo}
                    alt="Doxzon"
                    className="dox-logo h-9 w-auto"
                  />
                </button>
              </div>

              {/* Horizontal Navbar Links */}
              <ul className="flex items-center justify-center gap-4 list-none m-0 p-0 flex-1 px-4">
                {NAV_ITEMS.map((item) => (
                  <li key={item.label}>
                    <button
                      onClick={() => handleNav(item)}
                      className="dox-nav-link px-3 py-2 text-[14px] font-medium transition-all duration-150 whitespace-nowrap bg-transparent border-none cursor-pointer"
                      style={{ color: darkMode ? "#d4b060" : "#071a3d" }}
                    >
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}

          {/* ── SHARED RIGHT CORNER: Login Button ── */}
          <div className="shrink-0 flex items-center z-10 pl-1">
            <button
              onClick={() => syncLogin(true, true)}
              className="dox-login-btn px-4 sm:px-6 py-1.5 sm:py-2 rounded-full text-[13px] sm:text-[13.5px] font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap"
              style={{
                border: darkMode
                  ? "1.5px solid rgba(200,150,30,0.50)"
                  : "1.5px solid rgba(3,45,87,0.25)",
                background: darkMode
                  ? "rgba(200,150,30,0.08)"
                  : "rgba(255,255,255,0.6)",
                color: darkMode ? "#f0c040" : "#071a3d",
              }}
            >
              Login
            </button>
          </div>
        </div>
      </nav>

      {/* ── PREMIUM UPGRADED SIDE MENU DRAWER (image_aefadf.png Fix) ── */}
      <Drawer
        placement="left"
        open={mobileMenuOpen}
        closable={false}
        width={300}
        onClose={() => setMobileMenuOpen(false)}
        styles={{
          body: {
            padding: 0,
            overflow: "auto",
            background: "#f8fafc",
          },
        }}
      >
        {/* Premium Header */}
        <div
          style={{
            background:
              "linear-gradient(135deg,#022c43 0%,#0f766e 45%,#14b8a6 100%)",
            padding: " 18px 20px",
            position: "relative",
            minHeight: "130px",
            overflow: "hidden",
          }}
        >
          {/* Close */}
          <button
            onClick={() => setMobileMenuOpen(false)}
            style={{
              position: "absolute",
              right: 14,
              top: 14,
              width: 34,
              height: 34,
              borderRadius: "50%",
              border: "1px solid rgba(255,255,255,.15)",
              background: "rgba(255,255,255,.08)",
              backdropFilter: "blur(10px)",
              color: "#fff",
              cursor: "pointer",
            }}
          >
            <X size={18} />
          </button>

          {/* Doxzon Logo */}
          <img
            src={logo}
            alt="Doxzon"
            style={{
              height: "40px",
              objectFit: "contain",
              width: "auto",
              marginBottom: "10px",
              filter: "brightness(0) invert(1)",
            }}
          />

          <div
            style={{
              color: "#fff",
              fontSize: "17px",
              fontWeight: 600,
            }}
          >
            Healthcare. Simplified.
          </div>
        </div>

        {/* Menu Items */}
        <div style={{ padding: 18 }}>
          {NAV_ITEMS.map((item) => (
            <button
              key={item.label}
              onClick={() => handleNav(item)}
              style={{
                width: "100%",
                border: "none",
                cursor: "pointer",
                borderRadius: 18,
                background: "#fff",
                marginBottom: 10,
                padding: "12px 14px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                boxShadow: "0 4px 20px rgba(0,0,0,.04)",
                transition: ".3s",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                }}
              >
                <div
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: 12,
                    background: "linear-gradient(135deg,#ecfeff,#d1fae5)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#0f766e",
                  }}
                >
                  {MENU_ICONS[item.label.trim()]}
                </div>

                <span
                  style={{
                    fontSize: 16,
                    fontWeight: 600,
                    color: "#0f172a",
                  }}
                >
                  {item.label}
                </span>
              </div>

              <ChevronRight size={24} color="#0f766e" />
            </button>
          ))}
        </div>

        {/* Bottom Card */}
        <div style={{ padding: "0 16px 16px" }}>
          <button
            onClick={() => syncLogin(true, true)}
            style={{
              width: "100%",
              height: "48px",
              border: "none",
              borderRadius: "14px",
              background: "linear-gradient(135deg,#0f766e,#14b8a6)",
              color: "#fff",
              fontWeight: 600,
              fontSize: "16px",
              cursor: "pointer",
            }}
          >
            Login / Sign Up
          </button>
        </div>
      </Drawer>

      {/* Login / Registration Modals */}
      <Modal
        open={loginOpen}
        footer={null}
        onCancel={() => syncLogin(false)}
        destroyOnClose
        className="responsive-modal"
      >
        <Login
          handleLoginModal={() => syncLogin(false)}
          handleSignup={() => {
            syncLogin(false);
            syncSignup(true);
          }}
          showUserTypeSwitch={showLoginUserTypeSwitch}
        />
      </Modal>

      <Modal
        open={signupOpen}
        footer={null}
        onCancel={() => syncSignup(false)}
        destroyOnClose
        width="30%"
      >
        <SignUp handleRegClose={() => syncSignup(false)} />
      </Modal>
    </>
  );
}

export default Navbar;
